import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { AuthContext } from '../lib/AuthContext';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const ClientBookings = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const categories = ['1:1 Calls', 'Priority DM', 'Webinar', 'Packages'];
  const tabs = ['upcoming', 'completed', 'live'];

  const fetchSessions = useCallback(async () => {
    if (!accessToken) {
      setError("You must be logged in to view bookings.");
      setLoading(false);
      return;
    }

    // Only show loader on initial fetch
    if (sessions.length === 0) {
        setLoading(true);
    }

    try {
      const response = await axios.get(`https://quirkleai.com/api/bookings/?status=${activeTab}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setSessions(response.data);
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError("Failed to load bookings. Please try again.");
    } finally {
        if (sessions.length === 0) {
            setLoading(false);
        }
    }
  }, [activeTab, accessToken, sessions.length]);

  useEffect(() => {
    fetchSessions(); // Initial fetch

    if (activeTab === 'live') {
      const intervalId = setInterval(() => {
        fetchSessions();
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(intervalId); // Cleanup on unmount or tab change
    }
  }, [activeTab, fetchSessions]);

  const handleJoinMeeting = (meetingId) => {
    if (!meetingId) {
      console.warn("Meeting ID is missing for this session. Cannot join meeting.");
      return;
    }
    navigate(`/client/jitsi-room/${meetingId}`);
  };

  const filterLiveSessions = (sessions) => {
    const now = new Date();
    return sessions.filter(session => {
      const startTime = parseISO(session.start_time);
      const endTime = parseISO(session.end_time);
      return now >= startTime && now <= endTime;
    });
  };

  const displayedSessions = activeTab === 'live' ? filterLiveSessions(sessions) : sessions;

  if (loading) {
    return <div className="text-center py-10">Loading bookings...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-600">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-2xl font-semibold text-gray-900">Bookings</h2>

      <div className="flex gap-2 flex-wrap">
        {categories.map((cat) => (
          <Button key={cat} variant="outline" size="sm">
            {cat}
          </Button>
        ))}
      </div>

      <div className="flex border-b border-gray-200 space-x-4 pt-4">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`text-sm font-medium pb-2 border-b-2 ${
              activeTab === tab
                ? 'text-indigo-600 border-indigo-600'
                : 'text-gray-500 border-transparent hover:text-indigo-600'
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {displayedSessions.length === 0 ? (
          <div className="text-center pt-10">
            <h3 className="text-lg font-semibold text-gray-600">No {activeTab} sessions found.</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mt-1">
              Find the right mental health professional and schedule personalized 1:1 sessions to support your growth.
            </p>
            <Button className="mt-4">Find a therapist</Button>
          </div>
        ) : (
          displayedSessions.map((session) => (
            <div key={session.id} className="border rounded-lg p-4 shadow-sm bg-white">
              <p className="font-semibold text-lg">Session with {session.therapist.first_name} {session.therapist.last_name}</p>
              <p className="text-gray-600">Service: {session.service?.name || 'N/A'}</p>
              <p className="text-gray-600">Date: {format(parseISO(session.start_time), 'PPP')}</p>
              <p className="text-gray-600">Time: {format(parseISO(session.start_time), 'p')} - {format(parseISO(session.end_time), 'p')}</p>
              {activeTab === 'live' && user?.role !== 'therapist' && (
                <Button
                  className="mt-2"
                  onClick={() => handleJoinMeeting(session.meeting_id)}
                  disabled={!session.is_meeting_active}
                >
                  {session.is_meeting_active ? "Join Meeting" : "Waiting for Therapist"}
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClientBookings;